import { DOCUMENT_SIGNATURE_PREFIX } from '@worki/shared';
import { SENDER_SIGNATURE_MAX_CHARS } from './anchors.js';

/**
 * 1.1, a third time — the sender's own signatures, kept to use again.
 *
 * Nitzan, 17.09: the signature dialog keeps a signature to pull out on the next
 * document. It is kept on this device, the user's choice that day, and not in
 * the account: a phone and a computer each keep their own.
 *
 * 1.28 — Nitzan, 30.09: not one signature but a list of up to five, with a way
 * to add one and to remove one. The list is shown latest first, and a sixth
 * pushes the oldest out.
 *
 * It is kept under the signed-in user, so two people on one office computer are
 * never offered each other's signatures. A browser can refuse storage at any
 * time (private mode, a full disk, a blocked site), so every call says whether
 * it worked and none of them throws.
 */

/** The part of the browser's storage this needs; handed in so it can be tried without one. */
export type SignatureStore = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/** The most signatures one person keeps on a device. */
export const SAVED_SIGNATURES_MAX = 5;

const keyFor = (owner: string) => `worki.signing.savedSignatures.${owner}`;

/** Where the one signature was kept before there was a list. */
const singleKeyFor = (owner: string) => `worki.signing.savedSignature.${owner}`;

/** Only a picture the server takes on a box: a PNG within the signature cap. */
const keepable = (image: unknown): image is string =>
  typeof image === 'string' && image.startsWith(DOCUMENT_SIGNATURE_PREFIX) && image.length <= SENDER_SIGNATURE_MAX_CHARS;

/** The list as it was stored: whatever in it is not a picture the server takes is left out, never thrown on. */
function readList(stored: string): string[] {
  try {
    const list: unknown = JSON.parse(stored);
    return Array.isArray(list) ? [...new Set(list.filter(keepable))].slice(0, SAVED_SIGNATURES_MAX) : [];
  } catch {
    return [];
  }
}

/**
 * The signatures `owner` kept on this device, the latest first; none when there
 * are none.
 *
 * The first read after the list came in turns the one signature kept before it
 * into the list's first, and its own key goes. The list is stored even when it
 * is empty, so one taken away is not brought back by an old key left behind.
 */
export function savedSignatures(store: SignatureStore | null, owner: string): string[] {
  if (!store) return [];
  try {
    const stored = store.getItem(keyFor(owner)) ?? null;
    if (stored !== null) return readList(stored);

    const earlier = store.getItem(singleKeyFor(owner));
    if (!keepable(earlier)) return [];
    try {
      store.setItem(keyFor(owner), JSON.stringify([earlier]));
      store.removeItem(singleKeyFor(owner));
    } catch {
      // Not moved yet: it is offered all the same, and moved on a later read.
    }
    return [earlier];
  } catch {
    return [];
  }
}

/**
 * Keeps `image` as the latest of `owner`'s signatures on this device; false
 * when it was not kept. The oldest goes when there would be a sixth, and a
 * picture already in the list is moved to the front rather than kept twice.
 */
export function rememberSignature(store: SignatureStore | null, owner: string, image: string): boolean {
  if (!store || !keepable(image)) return false;
  try {
    const list = [image, ...savedSignatures(store, owner).filter((kept) => kept !== image)];
    store.setItem(keyFor(owner), JSON.stringify(list.slice(0, SAVED_SIGNATURES_MAX)));
    return true;
  } catch {
    return false;
  }
}

/** Removes `image` from `owner`'s signatures on this device; false when it could not be removed. */
export function forgetSignature(store: SignatureStore | null, owner: string, image: string): boolean {
  if (!store) return false;
  try {
    const list = savedSignatures(store, owner).filter((kept) => kept !== image);
    store.setItem(keyFor(owner), JSON.stringify(list));
    return true;
  } catch {
    return false;
  }
}
